<template>
  <div class="login-page">
    <!-- Navigation -->
    <nav class="nav">
      <div class="nav-inner">
        <div class="nav-left">
          <el-image class="nav-logo" style="width: 40px;height: 40px;"
            :src="require('@/assets/img/qhy2.png')"></el-image>
          <span class="nav-name">{{ $siteConfig.siteName }}</span>
        </div>
        <div class="nav-right">
          <button class="btn-text" @click="$router.push('/')">首页</button>
          <button class="btn-primary" @click="$router.push('/register')">注册</button>
        </div>
      </div>
    </nav>

    <!-- Login Form -->
    <section class="login-section">
      <div class="login-bg">
        <div class="blob blob-1"></div>
        <div class="blob blob-2"></div>
      </div>
      <div class="login-content">
        <div class="login-card">
          <div class="login-header">
            <div class="login-badge">
              <app-icon  class="app-icon badge-icon" name="login"></app-icon>
              <span>欢迎回来</span>
            </div>
            <h1 class="login-title">登录{{ $siteConfig.siteName }}</h1>
            <p class="login-desc">继续您的智能视频创作之旅</p>
          </div>

          <el-form :model="form" :rules="rules" ref="loginForm" class="login-form">
            <div class="form-group">
              <label class="form-label">
                <app-icon  class="app-icon label-icon" name="person"></app-icon>
                手机号/用户名
              </label>
              <el-form-item prop="account">
                <el-input v-model="form.account" placeholder="请输入手机号或用户名"></el-input>
              </el-form-item>
            </div>

            <div class="form-group">
              <div class="form-label-row">
                <label class="form-label">
                  <app-icon  class="app-icon label-icon" name="lock"></app-icon>
                  密码
                </label>
                <el-link type="primary" class="forgot-link" @click="$router.push('/password?mode=forgot')">忘记密码？</el-link>
              </div>
              <el-form-item prop="password">
                <el-input type="password" v-model="form.password" placeholder="请输入密码" show-password
                  @keyup.native.enter="handleLogin"></el-input>
              </el-form-item>
            </div>

            <el-form-item>
              <button type="button" class="btn-login" @click="handleLogin" :disabled="loading">
                <app-icon v-if="loading"  class="app-icon spinning" name="progress_activity"></app-icon>
                <span v-else>登录</span>
              </button>
            </el-form-item>

            <div class="form-footer">
              <span>没有账户？</span>
              <el-link type="primary" @click="$router.push('/register')">立即注册</el-link>
            </div>

            <div class="form-agreement">
              登录表示您同意我们的
              <el-link type="primary" @click="showTerms = true">《用户条款》</el-link>
              和
              <el-link type="primary" @click="showPrivacy = true">《隐私协议》</el-link>
            </div>
          </el-form>
        </div>
      </div>
    </section>

    <!-- Dialogs -->
    <el-dialog title="《用户条款》" :visible.sync="showTerms" append-to-body custom-class="terms-dialog">
      <div style="height: 500px;">
        <iframe :src="`/user_service?appname=${$siteConfig.siteName}&companyname=`" frameborder="0"
          style="width: 100%;height: 100%;"></iframe>
      </div>
    </el-dialog>
    <el-dialog title="《隐私协议》" :visible.sync="showPrivacy" append-to-body custom-class="terms-dialog">
      <div style="height: 500px;">
        <iframe :src="`/yszc?appname=${$siteConfig.siteName}&companyname=`" frameborder="0"
          style="width: 100%;height: 100%;"></iframe>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { setSiteVersion } from '@/config/siteVersion'

export default {
  name: 'Login',
  data() {
    return {
      loading: false,
      showTerms: false,
      showPrivacy: false,
      form: {
        account: '',
        password: ''
      },
      rules: {
        account: [
          { required: true, message: '请输入手机号或用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    handleLogin() {
      this.$refs.loginForm.validate(async (valid) => {
        if (!valid) return

        this.loading = true
        try {
          const res = await this.$ajax.post('api/v1/users/login', {
            account: this.form.account,
            password: this.form.password
          })

          if (res.data.access_token) {
            window.localStorage.setItem('token', res.data.access_token)
            window.localStorage.removeItem('end_time')
            // 登录成功后清除邀请信息
            window.sessionStorage.removeItem('upid')
            this.$msg.success('登录成功！')

            const redirect = this.$route.query.redirect
            // 登录后统一进 2.0；只有带 redirect（被守卫拦下的目标页）时才回到原页面
            setSiteVersion('2')
            this.$router.push(redirect || '/user2/index')
          } else {
            this.$msg.error(res.data.msg || res.data.detail || '登录失败')
          }
        } catch (err) {
          const data = err.response && err.response.data
          const msg = (data && (data.msg || data.detail)) || '登录失败，请稍后重试'
          this.$msg.error(msg)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
/* ===== Design Tokens ===== */
.login-page {
  --c-primary: #003ec7;
  --c-primary-container: #0052ff;
  --c-primary-fixed-dim: #1a73e8;
  --c-white: #ffffff;
  --c-deep: #f3f6fb;
  --c-mid: #ffffff;
  --c-slate-300: #475569;
  --c-slate-400: #64748b;
  --font-heading: 'Hanken Grotesk', sans-serif;
  --font-body: 'Inter', sans-serif;
  --font-label: 'Geist', sans-serif;
  --space-xs: 4px;
  --space-base: 8px;
  --space-sm: 12px;
  --space-md: 24px;
  --space-lg: 48px;
  min-height: 100vh;
  background: var(--c-deep);
  color: #1e293b;
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
}

/* ===== Navigation ===== */
.nav {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 50;
  background: rgba(246, 249, 253, 0.88);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.05);
}

.nav-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 var(--space-md);
  height: 64px;
}

.nav-left {
  display: flex;
  align-items: center;
  gap: var(--space-base);
}

.nav-logo {
  height: 40px;
  width: 40px;
  object-fit: contain;
}

.nav-name {
  font-family: var(--font-heading);
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.btn-text {
  background: none;
  border: none;
  color: var(--c-slate-300);
  font-family: var(--font-label);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;
}

.btn-text:hover {
  color: #1e293b;
}

.btn-primary {
  background: var(--c-primary-container);
  color: var(--c-white);
  border: none;
  padding: var(--space-sm) var(--space-lg);
  border-radius: 8px;
  font-family: var(--font-label);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  filter: brightness(1.1);
}

/* ===== Login Section ===== */
.login-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px var(--space-md) var(--space-lg);
  overflow: hidden;
  background: linear-gradient(145deg, #f6f9fd 0%, #eaf1fb 55%, #f3f6fb 100%);
}

.login-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.5;
  animation: blobMove 25s infinite alternate ease-in-out;
}

.blob-1 {
  background: #93c5fd;
  width: 600px;
  height: 600px;
  top: -100px;
  left: -100px;
}

.blob-2 {
  background: #bfdbfe;
  width: 500px;
  height: 500px;
  bottom: -50px;
  right: -50px;
  animation-delay: -10s;
}

@keyframes blobMove {
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(5%, 10%) scale(1.1); }
  100% { transform: translate(-5%, 5%) scale(1); }
}

.login-content {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 420px;
}

/* ===== Login Card ===== */
.login-card {
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 24px 70px rgba(51, 65, 85, 0.12);
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: 4px 12px;
  background: #eff6ff;
  border-radius: 9999px;
  border: 1px solid #bfdbfe;
  margin-bottom: 16px;
  font-family: var(--font-label);
  font-size: 12px;
  color: var(--c-slate-300);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.badge-icon {
  font-size: 16px;
  color: var(--c-primary-fixed-dim);
}

.login-title {
  font-family: var(--font-heading);
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.login-desc {
  font-size: 14px;
  color: var(--c-slate-400);
  margin: 0;
}

/* ===== Form Styles ===== */
.login-form {
  margin-top: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-label-row .form-label {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-label);
  font-size: 13px;
  font-weight: 500;
  color: var(--c-slate-300);
  margin-bottom: 8px;
  letter-spacing: 0.03em;
}

.forgot-link {
  font-size: 12px;
}

.label-icon {
  font-size: 16px;
  color: var(--c-primary-fixed-dim);
}

/* Element UI Input Override */
.login-form >>> .el-input__inner {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  color: #1e293b;
  height: 44px;
  font-size: 14px;
  transition: all 0.2s;
}

.login-form >>> .el-input__inner::placeholder {
  color: var(--c-slate-400);
}

.login-form >>> .el-input__inner:focus {
  border-color: var(--c-primary-container);
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(0, 82, 255, 0.2);
}

.login-form >>> .el-form-item__error {
  font-size: 12px;
  padding-top: 4px;
}

/* Login Button */
.btn-login {
  width: 100%;
  height: 48px;
  background: var(--c-primary-container);
  color: var(--c-white);
  border: none;
  border-radius: 10px;
  font-family: var(--font-heading);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-login:hover {
  box-shadow: 0 20px 40px -12px rgba(0, 82, 255, 0.4);
  transform: translateY(-2px);
}

.btn-login:active {
  transform: translateY(0);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Footer */
.form-footer {
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: var(--c-slate-400);
}

.form-footer >>> .el-link {
  font-size: 14px;
  margin-left: 4px;
}

.form-agreement {
  text-align: center;
  margin-top: 24px;
  font-size: 13px;
  color: var(--c-slate-400);
}

.form-agreement >>> .el-link {
  font-size: 13px;
}

/* Dialog Override */
.login-page >>> .terms-dialog {
  background: var(--c-mid);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.login-page >>> .el-dialog__title {
  color: #1e293b;
}

.login-page >>> .el-dialog__header {
  border-bottom: 1px solid #e2e8f0;
}

/* ===== Responsive ===== */
@media (max-width: 640px) {
  .nav-inner {
    padding: 0 16px;
  }

  .nav-name {
    font-size: 18px;
  }

  .nav-logo {
    width: 28px !important;
    height: 28px !important;
  }

  .login-section {
    align-items: flex-start;
    padding: 90px 16px 32px;
  }

  .login-card {
    padding: 28px 20px;
  }

  .login-title {
    font-size: 24px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Login.vue